import { useEffect, useMemo, useState } from "react";
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { useDroppable } from "@dnd-kit/core";
import { useDraggable } from "@dnd-kit/core";
import { motion } from "framer-motion";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Plus,
  User,
  Bot,
  Clock,
  Link2,
  PanelRightOpen,
  ArrowRight,
  ArrowLeft,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { api, Task, TaskBucket, TaskPriority } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import TaskDetailModal from "@/components/TaskDetailModal";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

type TaskOwner = "arek" | "kasia";
type OwnerFilter = "all" | TaskOwner;

const OWNER_FILTERS: { id: OwnerFilter; label: string }[] = [
  { id: "all", label: "Wszyscy" },
  { id: "arek", label: "Arek" },
  { id: "kasia", label: "Kasia" },
];

const OWNER_CARD_STYLES: Record<TaskOwner, string> = {
  arek: "border-blue-200 bg-blue-50 hover:border-blue-300",
  kasia: "border-pink-200 bg-pink-50 hover:border-pink-300",
};

const PRIORITY_BADGE: Record<TaskPriority, string> = {
  P0: "bg-rose-100 text-rose-800",
  P1: "bg-amber-100 text-amber-900",
  P2: "bg-slate-200 text-slate-700",
};

const columns: { bucket: TaskBucket; title: string; short: string; accent: string; rail: string }[] = [
  { bucket: "todo", title: "ToDo", short: "ToDo", accent: "text-slate-600", rail: "border-l-slate-400" },
  {
    bucket: "in_progress",
    title: "In Progress",
    short: "W toku",
    accent: "text-brand",
    rail: "border-l-brand",
  },
  { bucket: "done", title: "Done", short: "Done", accent: "text-emerald-700", rail: "border-l-emerald-600" },
];

const BUCKET_ORDER: TaskBucket[] = ["todo", "in_progress", "done"];

function resolveOwner(task: Task): TaskOwner {
  if (task.owner === "kasia" || task.owner === "arek") return task.owner;
  const hint = `${task.agent || ""} ${task.title || ""} ${task.description || ""}`.toLowerCase();
  if (hint.includes("kasia") || /\bkasi\b/.test(hint)) return "kasia";
  if (hint.includes("arek")) return "arek";
  return "arek";
}

function resolvePriority(task: Task): TaskPriority {
  if (task.priority === "P0" || task.priority === "P1" || task.priority === "P2") {
    return task.priority;
  }
  return "P1";
}

function isDeadlineOverdue(deadline: string | null | undefined): boolean {
  if (!deadline) return false;
  const m = deadline.trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return false;
  const end = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 23, 59, 59);
  return end.getTime() < Date.now();
}

function useIsMobileMd(): boolean {
  const [mobile, setMobile] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia("(max-width: 767px)").matches : false,
  );
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const onChange = () => setMobile(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return mobile;
}

function nextBucket(bucket: TaskBucket): TaskBucket | null {
  const i = BUCKET_ORDER.indexOf(bucket);
  return i >= 0 && i < BUCKET_ORDER.length - 1 ? BUCKET_ORDER[i + 1] : null;
}

function prevBucket(bucket: TaskBucket): TaskBucket | null {
  const i = BUCKET_ORDER.indexOf(bucket);
  return i > 0 ? BUCKET_ORDER[i - 1] : null;
}

export default function Kanban() {
  const qc = useQueryClient();
  const isMobile = useIsMobileMd();
  const [ownerFilter, setOwnerFilter] = useState<OwnerFilter>("arek");
  const [newTitle, setNewTitle] = useState("");
  const [newOwner, setNewOwner] = useState<TaskOwner>("arek");
  const [activeId, setActiveId] = useState<string | null>(null);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [mobileBucket, setMobileBucket] = useState<TaskBucket>("todo");

  const { data: tasks = [] } = useQuery({
    queryKey: ["tasks", ownerFilter],
    queryFn: () => api.listTasks(ownerFilter === "all" ? undefined : ownerFilter),
  });

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const byBucket = useMemo(() => {
    const m: Record<TaskBucket, Task[]> = { todo: [], in_progress: [], done: [] };
    for (const t of tasks) m[t.bucket].push(t);
    return m;
  }, [tasks]);

  const detailTask = detailId ? tasks.find((t) => t.id === detailId) || null : null;

  const mMove = useMutation({
    mutationFn: ({ id, bucket }: { id: string; bucket: TaskBucket }) => api.moveTask(id, bucket),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tasks"] }),
  });
  const mCreate = useMutation({
    mutationFn: () =>
      api.createTask({
        title: newTitle,
        task_type: "human",
        bucket: "todo" as TaskBucket,
        owner: isMobile
          ? ownerFilter === "all"
            ? newOwner
            : ownerFilter
          : newOwner,
        priority: "P1",
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tasks"] });
      setNewTitle("");
      if (isMobile) setMobileBucket("todo");
    },
  });

  const setPersonMobile = (owner: TaskOwner) => {
    setOwnerFilter(owner);
    setNewOwner(owner);
  };

  const onDragStart = (e: DragStartEvent) => setActiveId(String(e.active.id));
  const onDragEnd = (e: DragEndEvent) => {
    setActiveId(null);
    if (!e.over) return;
    const taskId = String(e.active.id);
    const bucket = e.over.id as TaskBucket;
    mMove.mutate({ id: taskId, bucket });
  };

  const activeTask = tasks.find((t) => t.id === activeId);

  const createForm = (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (newTitle.trim()) mCreate.mutate();
      }}
      className="flex w-full flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center"
    >
      {!isMobile ? (
        <div className="flex items-center gap-1 rounded-lg border border-slate-200 bg-white p-0.5">
          {(["arek", "kasia"] as TaskOwner[]).map((owner) => (
            <Button
              key={owner}
              type="button"
              size="sm"
              variant={newOwner === owner ? "default" : "ghost"}
              className={cn(
                "h-8 px-3",
                newOwner === owner && owner === "arek" && "bg-blue-600 hover:bg-blue-700",
                newOwner === owner && owner === "kasia" && "bg-pink-600 hover:bg-pink-700",
              )}
              onClick={() => setNewOwner(owner)}
            >
              {owner === "arek" ? "Arek" : "Kasia"}
            </Button>
          ))}
        </div>
      ) : null}
      <div className="flex min-w-0 flex-1 gap-2">
        <Input
          placeholder="Nowe zadanie…"
          value={newTitle}
          onChange={(e) => setNewTitle(e.target.value)}
          className="min-w-0 flex-1 bg-white"
        />
        <Button type="submit" size="sm" variant="default" className="shrink-0">
          <Plus className="mr-1 h-3.5 w-3.5" /> Dodaj
        </Button>
      </div>
    </form>
  );

  return (
    <div className="mx-0 max-w-full overflow-x-hidden rounded-2xl border border-border bg-[#f7f8fa] p-3 text-foreground shadow-sm sm:p-6">
      <div className={cn(isMobile && "mb-3")}>
        <PageHeader
          title="Kanban"
          subtitle={isMobile ? undefined : "Zadania z bota Telegram + agenci AI"}
          actions={!isMobile ? createForm : undefined}
        />
      </div>

      {isMobile ? (
        <div className="sticky top-14 z-20 mb-3 w-full space-y-3 border-b border-slate-200 bg-[#f7f8fa] pb-3 pt-1">
          <div className="grid grid-cols-2 gap-1 rounded-xl border border-slate-200 bg-white p-1">
            {(["arek", "kasia"] as TaskOwner[]).map((owner) => (
              <Button
                key={owner}
                type="button"
                size="sm"
                variant={ownerFilter === owner ? "default" : "ghost"}
                className={cn(
                  "h-11 text-base font-semibold",
                  ownerFilter === owner && owner === "arek" && "bg-blue-600 hover:bg-blue-700",
                  ownerFilter === owner && owner === "kasia" && "bg-pink-600 hover:bg-pink-700",
                )}
                onClick={() => setPersonMobile(owner)}
              >
                {owner === "arek" ? "Arek" : "Kasia"}
              </Button>
            ))}
          </div>
          <button
            type="button"
            className={cn(
              "text-xs font-medium text-slate-500 underline-offset-2 hover:underline",
              ownerFilter === "all" && "text-slate-800",
            )}
            onClick={() => setOwnerFilter("all")}
          >
            {ownerFilter === "all" ? "Widok: wszyscy" : "Pokaż wszystkich"}
          </button>
          {createForm}
        </div>
      ) : (
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium uppercase tracking-wide text-slate-500">Filtr</span>
          {OWNER_FILTERS.map((row) => (
            <Button
              key={row.id}
              size="sm"
              variant={ownerFilter === row.id ? "brand" : "outline"}
              className={cn(
                ownerFilter === row.id && row.id === "arek" && "border-blue-300 bg-blue-600 hover:bg-blue-700",
                ownerFilter === row.id && row.id === "kasia" && "border-pink-300 bg-pink-600 hover:bg-pink-700",
              )}
              onClick={() => setOwnerFilter(row.id)}
            >
              {row.label}
            </Button>
          ))}
        </div>
      )}

      {isMobile ? (
        <>
          <div className="mb-3 grid grid-cols-3 gap-1 rounded-xl border border-slate-200 bg-white p-1">
            {columns.map((col) => (
              <button
                key={col.bucket}
                type="button"
                className={cn(
                  "flex flex-col items-center rounded-lg px-1 py-2 text-xs font-semibold transition-colors",
                  mobileBucket === col.bucket
                    ? "bg-slate-900 text-white"
                    : "text-slate-600 hover:bg-slate-50",
                )}
                onClick={() => setMobileBucket(col.bucket)}
              >
                <span>{col.short}</span>
                <span
                  className={cn(
                    "mt-0.5 text-[10px] font-medium",
                    mobileBucket === col.bucket ? "text-white/80" : "text-slate-400",
                  )}
                >
                  {byBucket[col.bucket].length}
                </span>
              </button>
            ))}
          </div>
          <div className="space-y-2">
            {byBucket[mobileBucket].map((t) => (
              <TaskCard
                key={t.id}
                task={t}
                mobile
                onOpenDetails={setDetailId}
                onMove={(bucket) => mMove.mutate({ id: t.id, bucket })}
                moving={mMove.isPending}
              />
            ))}
            {byBucket[mobileBucket].length === 0 ? (
              <div className="rounded-lg border border-dashed border-slate-200 bg-white/70 py-12 text-center text-xs text-slate-500">
                Brak zadań w tej kolumnie
              </div>
            ) : null}
          </div>
        </>
      ) : (
        <DndContext sensors={sensors} onDragStart={onDragStart} onDragEnd={onDragEnd}>
          <div className="grid gap-4 md:grid-cols-3">
            {columns.map((col) => (
              <Column
                key={col.bucket}
                {...col}
                items={byBucket[col.bucket]}
                onOpenDetails={setDetailId}
              />
            ))}
          </div>
          <DragOverlay>{activeTask ? <TaskCard task={activeTask} overlay /> : null}</DragOverlay>
        </DndContext>
      )}

      {detailTask ? (
        <TaskDetailModal task={detailTask} onClose={() => setDetailId(null)} />
      ) : null}
    </div>
  );
}

function Column({
  bucket,
  title,
  accent,
  rail,
  items,
  onOpenDetails,
}: {
  bucket: TaskBucket;
  title: string;
  accent: string;
  rail: string;
  items: Task[];
  onOpenDetails: (id: string) => void;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: bucket });

  return (
    <Card
      className={
        "rounded-xl border border-slate-200 bg-[#e8eaee] shadow-none backdrop-blur-none border-l-4 " +
        rail
      }
    >
      <CardHeader className="flex flex-row items-center justify-between pb-3">
        <CardTitle className={accent + " font-semibold"}>{title}</CardTitle>
        <Badge variant="secondary" className="bg-white text-slate-700">
          {items.length}
        </Badge>
      </CardHeader>
      <CardContent>
        <div
          ref={setNodeRef}
          className={
            "min-h-[320px] space-y-2 rounded-lg border border-dashed border-transparent transition-colors " +
            (isOver ? "border-brand/50 bg-white/70" : "")
          }
        >
          {items.map((t) => (
            <TaskCard key={t.id} task={t} onOpenDetails={onOpenDetails} />
          ))}
          {items.length === 0 && (
            <div className="py-10 text-center text-xs text-slate-500">
              brak zadań. Przeciągnij tu albo dodaj nowe
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function TaskCard({
  task,
  overlay = false,
  mobile = false,
  onOpenDetails,
  onMove,
  moving = false,
}: {
  task: Task;
  overlay?: boolean;
  mobile?: boolean;
  onOpenDetails?: (id: string) => void;
  onMove?: (bucket: TaskBucket) => void;
  moving?: boolean;
}) {
  const draggable = useDraggable({ id: task.id, disabled: mobile });
  const { attributes, listeners, setNodeRef, isDragging } = draggable;
  const opacity = isDragging ? 0.4 : 1;
  const SourceIcon = task.task_type === "human" ? User : Bot;
  const owner = resolveOwner(task);
  const ownerLabel = owner === "kasia" ? "Kasia" : "Arek";
  const priority = resolvePriority(task);
  const links = task.links || [];
  const overdue = isDeadlineOverdue(task.deadline);
  const forward = nextBucket(task.bucket);
  const back = prevBucket(task.bucket);

  return (
    <motion.div
      ref={mobile ? undefined : setNodeRef}
      {...(mobile ? {} : listeners)}
      {...(mobile ? {} : attributes)}
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity, y: 0 }}
      className={cn(
        "group rounded-md border p-3 text-sm text-slate-900",
        OWNER_CARD_STYLES[owner],
        !mobile && "cursor-grab active:cursor-grabbing",
        overlay && "rotate-1 border-slate-300",
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1 font-medium">{task.title}</div>
        <div className="flex shrink-0 items-center gap-1">
          <Badge variant="secondary" className={cn("border-0 text-[10px] font-medium", PRIORITY_BADGE[priority])}>
            {priority}
          </Badge>
          <Badge
            variant="secondary"
            className={cn(
              "border-0 text-[10px] font-medium",
              owner === "arek" ? "bg-blue-100 text-blue-800" : "bg-pink-100 text-pink-800",
            )}
          >
            {ownerLabel}
          </Badge>
          {onOpenDetails ? (
            <button
              type="button"
              title="Szczegóły"
              className="inline-flex h-7 w-7 items-center justify-center rounded-md text-slate-500 hover:bg-white/80 hover:text-slate-800"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                onOpenDetails(task.id);
              }}
            >
              <PanelRightOpen className="h-3.5 w-3.5" />
            </button>
          ) : null}
        </div>
      </div>
      {task.description ? (
        <div className="mt-1 line-clamp-2 text-xs text-muted-foreground">{task.description}</div>
      ) : null}
      <div className="mt-2 flex items-center gap-2 text-[11px] text-muted-foreground">
        <SourceIcon className="h-3 w-3" />
        <span>{task.agent ?? task.task_type}</span>
        {links.length > 0 ? (
          <span className="inline-flex items-center gap-0.5 text-slate-600" title={`${links.length} link(ów)`}>
            <Link2 className="h-3 w-3" />
            {links.length}
          </span>
        ) : null}
        {task.deadline ? (
          <span
            className={cn(
              "ml-auto inline-flex items-center gap-1",
              overdue ? "font-medium text-rose-700" : "",
            )}
          >
            <Clock className="h-3 w-3" /> {task.deadline}
          </span>
        ) : null}
      </div>
      {mobile && onMove ? (
        <div className="mt-3 flex gap-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-9 flex-1 bg-white"
            disabled={!back || moving}
            onClick={() => back && onMove(back)}
          >
            <ArrowLeft className="mr-1 h-3.5 w-3.5" />
            {back === "todo" ? "ToDo" : back === "in_progress" ? "W toku" : "Wstecz"}
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-9 flex-1 bg-white"
            disabled={!forward || moving}
            onClick={() => forward && onMove(forward)}
          >
            {forward === "in_progress" ? "W toku" : forward === "done" ? "Done" : "Dalej"}
            <ArrowRight className="ml-1 h-3.5 w-3.5" />
          </Button>
        </div>
      ) : null}
    </motion.div>
  );
}
